/**
 * Temporary screen-scoped design tokens for **Settings → Support Tickets →
 * Create Ticket**.
 *
 * The dialog is the same surface as the tickets list and the ticket detail
 * screen, so its ink, accent, field chrome and type steps are the values
 * `SupportTickets.tokens.ts` / `ViewTicket.tokens.ts` already carry. Once every
 * settings screen has been re-skinned these get promoted into the MUI theme and
 * this file is deleted.
 */

import {
  formField,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const createTicketTokens = {
  color: {
    ink: signer.ink,
    /** Sub-heading under the title, secondary button ink. */
    muted: signer.text.muted,
    body: signer.text.secondary,
    /** Placeholder text, counter, attach hint, close glyph. */
    subtle: signer.text.faint,
    placeholder: formField.placeholderMuted,
    surface: white,
    /** Field + editor outline. */
    fieldBorder: formField.border,
    fieldBg: formField.bg,
    /** Rule between the Quill toolbar and its body. */
    hairline: signer.border.light,
    /** Dashed attachment strip. */
    dropzoneBorder: switchControl.trackOff,
    dropzoneBg: formField.bg,
    accent: signer.orange.main,
    accentHover: signer.orange.dark,
    onAccent: white,
    error: notification.error.icon,
    toolbarHoverBg: signer.border.divider,
    closeHoverBg: signer.surface.muted,
    /** Chips for files already uploaded to the draft ticket. */
    chipBg: signer.surface.muted,
    chipText: signer.text.secondary,
  },

  font: {
    title: "20px",
    subtitle: "12.5px",
    label: "13px",
    field: "13px",
    counter: "11.5px",
    helper: "11.5px",
    hint: "12.5px",
    hintMeta: "12px",
    button: "12.5px",
    chip: "12px",
  },

  weight: {
    heading: 800,
    strong: 700,
    medium: 600,
    regular: 500,
  },

  letterSpacing: {
    title: "-0.01em",
  },

  radius: {
    dialog: "16px",
    control: "10px",
    editor: "10px",
    toolbarButton: "7px",
    pill: "999px",
  },

  /** Title, sub-heading and the close affordance. */
  header: {
    titleGap: "6px",
    marginBottom: "22px",
    closeSize: 32,
    closeIconSize: 18,
  },

  layout: {
    /** The design's dialog is a touch wider than MUI's `sm` breakpoint. */
    maxWidth: 660,
    padding: "26px 28px 24px",
    /** Vertical rhythm between one labelled field and the next. */
    fieldGap: "18px",
    /** Category / Priority side by side. */
    columnGap: "18px",
    labelMarginBottom: "8px",
    helperMarginTop: "6px",
  },

  field: {
    height: "44px",
    paddingX: "14px",
    /** Trailing counter inside the summary field. */
    adornmentGap: "8px",
    menuMaxHeight: 264,
    menuItemHeight: "auto",
  },

  /** Rich-text description box — same chrome as the ticket comment editor. */
  editor: {
    toolbarPadding: "6px 10px",
    toolbarGroupGap: "8px",
    buttonSize: 28,
    buttonIconSize: 15,
    minHeight: "150px",
    contentPadding: "14px 16px",
    lineHeight: 1.6,
  },

  /** One-line dashed "Attach a file" strip. */
  dropzone: {
    padding: "15px 16px",
    gap: "10px",
    iconSize: 16,
    chipsMarginTop: "10px",
    chipGap: "8px",
    supportedMarginTop: "8px",
    maxFileSizeLabel: "up to 2 MB",
    maxFileSizeBytes: 2 * 1024 * 1024,
  },

  footer: {
    marginTop: "24px",
    gap: "10px",
    buttonHeight: 40,
    buttonPaddingX: "20px",
  },
} as const;

export default createTicketTokens;
